// ADR 10.9.26f §8 — the phones' names for the web's first upgrade route.
//
// Nothing here is new vocabulary. `subscription_screen_viewed`,
// `subscription_started` and `subscription_completed` are
// `AnalyticsServiceSubscription.kt:10,22,28`'s; `upgrade_prompt_shown` and
// `upgrade_prompt_suppressed` are `AnalyticsService.kt:133,181`'s. So the web's
// rows land in series an operator already reads rather than beside them, and
// `upgrade_cta_*` appears nowhere: it would be a fourth name for a thing already
// named three times. `capture()` stamps `client: 'web'`, which is the axis that
// keeps the three clients apart (6.9.26c §1).
//
// **Every event below is dark for a denied session.** The web's PostHog default
// is *denied* (6.9.26c), so the funnel these three build has the consenting
// population as its denominator, not the population — worth knowing before a
// web conversion rate is compared to Android's (10.9.26f Consequences).
//
// A wrapper over `capture()` rather than an injectable sink, in
// `shareAnalytics.ts`'s shape: `capture()` is already a no-op with nothing
// attached, and the suite observes it by attaching a fake.

import { capture } from '../../telemetry/capture'
import type { SuppressionReason } from '../../wasm'

/**
 * The ten names, as constants so a typo is a type error. The last four are
 * ADR 14.10.26l §6's: the in-page checkout's open and cancel, and Android's
 * two challenge events (`AnalyticsServiceChallenge.kt`), so the web joins
 * those series rather than starting its own.
 */
export const BILLING_EVENTS = {
  screenViewed: 'subscription_screen_viewed',
  started: 'subscription_started',
  completed: 'subscription_completed',
  pollingResolved: 'subscription_polling_resolved',
  promptShown: 'upgrade_prompt_shown',
  promptSuppressed: 'upgrade_prompt_suppressed',
  checkoutOpened: 'subscription_checkout_opened',
  checkoutCancelled: 'subscription_checkout_cancelled',
  challengePresented: 'subscription_challenge_presented',
  challengeResolved: 'subscription_challenge_resolved',
} as const

/**
 * How the return landing's poll ended — the pair `SubscriptionViewModel.kt`
 * passes (11.9.26g §1): `active` when a poll resolved, `pending` when the
 * schedule ran out. Exported beside `BILLING_EVENTS` so the test enumerates
 * the type rather than restating the union.
 */
export type PollResult = 'active' | 'pending'

/**
 * Where the flow began: the account page's picker (the upgrade page passes
 * `UPGRADE_PAGE_SOURCE`, ADR 1.10.26c §9). Android's `source` is the screen that opened its subscription screen,
 * so this is the same property with the same meaning.
 */
export const BILLING_SOURCE = 'account'

/** The throttle's `trigger` on this surface — the phones' is `home_banner`. */
export const ACCOUNT_TRIGGER = 'account_page'

/**
 * The picker rendered with plans in it — once per mount, not per render.
 * `source` defaults to the account page's, so its event is unchanged; the
 * upgrade page passes `UPGRADE_PAGE_SOURCE` (ADR 8.10.26z §1).
 */
export function subscriptionScreenViewed(source: string = BILLING_SOURCE): void {
  capture(BILLING_EVENTS.screenViewed, { source })
}

/**
 * A plan was chosen and `create` is being called. Emitted *before* the request
 * rather than after: a learner who chose and was refused (a dark kill switch,
 * AC9) still started, and the funnel's second step is the choice.
 *
 * `plan_id` is a web-only property; Android's `subscription_started` carries
 * `source` alone, and a shared event carrying a property some clients never
 * send is already the norm in both directions (`shareAnalytics.ts`).
 */
export function subscriptionStarted(planId: string, options: StartedOptions = {}): void {
  const { source = BILLING_SOURCE, emailType } = options
  capture(BILLING_EVENTS.started, {
    source,
    plan_id: planId,
    ...(emailType === undefined ? {} : { email_type: emailType }),
  })
}

/**
 * ADR 1.10.26c §9 — the `source` of a checkout started on the public upgrade
 * page, beside the account page's `BILLING_SOURCE`.
 */
export const UPGRADE_PAGE_SOURCE = 'upgrade_page'

/**
 * `source` defaults to the account page's, so its event is unchanged;
 * `emailType` is the trial mail that brought the reader (the page's `src`), and
 * becomes `email_type` only when present.
 */
export interface StartedOptions {
  source?: string
  emailType?: string
}

/**
 * The return landing's poll resolved to `status === 'active'` (§8) — a payment
 * the *server* has confirmed, not a return from Stripe. Android emits the same
 * event after its own confirmation, so the two funnels end on one definition.
 *
 * `platform` and `is_restore` are the properties Android's carries
 * (`AnalyticsServiceSubscription.kt:29-31`), spelled here so a breakdown on
 * either does not file the web under *absent*. There is nothing to restore on
 * the web, so `is_restore` is constant.
 *
 * `source` defaults to the account page's, so its emit is unchanged; since
 * ADR 14.10.26l §5 the upgrade page passes `UPGRADE_PAGE_SOURCE` when `confirm`
 * itself answered `active`.
 */
export function subscriptionCompleted(source: string = BILLING_SOURCE): void {
  capture(BILLING_EVENTS.completed, {
    source,
    platform: 'web',
    is_restore: false,
  })
}

/**
 * The return landing's poll ended, one way or the other (11.9.26g §1) — the
 * race 10.9.26f §3 made user-visible, timed. Android's properties
 * (`AnalyticsServiceSubscription.kt:70-77`) minus `plan_id`, which the return
 * URL does not carry. `poll_count` is the attempt that resolved it, or the
 * schedule's ceiling when none did, which is what Android reports; so for a
 * web row `(poll_count − 1) × interval` is a lower bound on the webhook's lag
 * behind the redirect.
 *
 * Since ADR 14.10.26l §6 it has a **second trigger** on the account page: the
 * in-page `settle()` after `confirm`. There `poll_count` is normally `1`
 * (`confirm` wrote `active` first), or it bounds the invoice webhook's lag
 * after a challenge. Split the two populations by whether
 * `subscription_checkout_opened {mode: 'elements'}` preceded it in the session.
 */
export function subscriptionPollingResolved(result: PollResult, pollCount: number): void {
  capture(BILLING_EVENTS.pollingResolved, {
    result,
    poll_count: pollCount,
    platform: 'web',
  })
}

/** Why the in-page checkout fell back to the hosted page (14.10.26g's two reasons). */
export type PlanFallbackReason = 'script_blocked' | 'unavailable'

/**
 * ADR 14.10.26l §6 — the plan checkout opened: the Payment Element mounted
 * (`elements`, 14.10.26n's console spelling), or the hosted fallback navigated
 * (with why). Read the fallback count as a floor: the web's default is denied.
 */
export function subscriptionCheckoutOpened(
  opened: { mode: 'elements' } | { mode: 'hosted_fallback'; reason: PlanFallbackReason },
  source: string,
): void {
  capture(BILLING_EVENTS.checkoutOpened, { ...opened, source })
}

/** ADR 14.10.26l §6 — Cancel pressed, or a plan change closed the open form. */
export function subscriptionCheckoutCancelled(source: string): void {
  capture(BILLING_EVENTS.checkoutCancelled, { source })
}

/** ADR 14.10.26l §6 — Android's event and properties: `confirm` returned a challenge secret. */
export function subscriptionChallengePresented(planId: string): void {
  capture(BILLING_EVENTS.challengePresented, { platform: 'web', plan_id: planId })
}

/**
 * ADR 14.10.26l §6 — Android's event and properties. `outcome` is the spelling
 * `postChallengeStep` accepted, which is `rust-core`'s `challenge_outcome_name`.
 */
export function subscriptionChallengeResolved(planId: string, outcome: string): void {
  capture(BILLING_EVENTS.challengeResolved, { platform: 'web', plan_id: planId, outcome })
}

/** The CTA rendered. Once per mount, beside `recordUpgradePrompt`. */
export function upgradePromptShown(): void {
  capture(BILLING_EVENTS.promptShown, { trigger: ACCOUNT_TRIGGER })
}

/**
 * The CTA was withheld — 1.9.26a D4's rule: only when an offer was actually
 * withheld, never on every render. `reason` and `seconds_remaining` are the
 * throttle's own pair, spelled as `AnalyticsServiceSession.kt:16-20` spells
 * them (`grace_period` / `cooldown`), which is also the wasm enum's `kind`.
 */
export function upgradePromptSuppressed(reason: SuppressionReason): void {
  capture(BILLING_EVENTS.promptSuppressed, {
    trigger: ACCOUNT_TRIGGER,
    reason: reason.kind,
    seconds_remaining: reason.secondsRemaining,
  })
}
